import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import GlobalStylesManager from './components/GlobalStylesManager';
import Home from './pages/Home';
import DashboardLayout from './pages/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Analyses from './pages/Analyses';
import Journal from './pages/Journal';
import Affiliates from './pages/Affiliates';
import Subscription from './pages/Subscription';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Login from './pages/Login';
import Register from './pages/Register';
import EmailConfirmation from './pages/EmailConfirmation';
import ResetPassword from './pages/ResetPassword';
import UpdatePassword from './pages/UpdatePassword';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookies from './pages/Cookies';
import Ambassadors from './pages/Ambassadors';
import Notifications from './pages/Notifications';
import BrokerConnection from './pages/BrokerConnection';
import TradingAccount from './pages/TradingAccount';

function App() {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const _shouldRedirectToDashboard = () => {
      const p = window.location.pathname;
      return p === '/' || p === '/login' || p === '/register';
    };

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        checkAdminStatus(session.user.id);
        if (_shouldRedirectToDashboard()) {
          window.location.href = '/dashboard';
          return;
        }
      }
      setLoading(false);
    });

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session) {
        checkAdminStatus(session.user.id);
        if (event === 'SIGNED_IN' && _shouldRedirectToDashboard()) {
          window.location.href = '/dashboard';
        }
      } else {
        setIsAdmin(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      if (!userId) return;

      const { data } = await supabase
        .from('admin_users')
        .select('id')
        .eq('id', userId);

      setIsAdmin(data && data.length > 0);
    } catch (err) {
      console.error('Error checking admin status:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-base flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-base flex flex-col">
      <GlobalStylesManager />
      <Routes>
        <Route path="/" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <Home />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="/dashboard/*" element={
          session ? (
            <DashboardLayout session={session} isAdmin={isAdmin}>
              <Routes>
                <Route index element={<Dashboard session={session} isAdmin={isAdmin} />} />
                <Route path="analyses" element={<Analyses />} />
                <Route path="journal" element={<Journal />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="affiliates" element={<Affiliates />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
                <Route path="broker-connection" element={<BrokerConnection />} />
                <Route path="trading-account" element={<TradingAccount />} />
                {isAdmin && <Route path="admin" element={<AdminPanel />} />}
              </Routes>
            </DashboardLayout>
          ) : (
            <Navigate to="/login" replace />
          )
        } />
        <Route path="/login" element={
          session ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <>
              <Navbar session={session} isAdmin={isAdmin} />
              <Login />
              <Footer />
              <CookieConsent />
            </>
          )
        } />
        <Route path="/register" element={
          session ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <>
              <Navbar session={session} isAdmin={isAdmin} />
              <Register />
              <Footer />
              <CookieConsent />
            </>
          )
        } />
        <Route path="/reset-password" element={
          session ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <>
              <Navbar session={session} isAdmin={isAdmin} />
              <ResetPassword />
              <Footer />
              <CookieConsent />
            </>
          )
        } />
        <Route path="/update-password" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <UpdatePassword />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="/email-confirmation" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <EmailConfirmation />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="/privacy" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <Privacy />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="/terms" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <Terms />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="/cookies" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <Cookies />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="/embajadores" element={
          <>
            <Navbar session={session} isAdmin={isAdmin} />
            <Ambassadors />
            <Footer />
            <CookieConsent />
          </>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;